import { Component, type SyntheticEvent } from "react";
import { observer } from "mobx-react";
import { Stack, DefaultButton, TextField, Separator, Icon, TooltipHost } from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import { createUuid } from "../../utils/uuid";
import { type ShockProfile, type ShockProfileCreate } from "../../api";
import { type FormCreateProps } from "../../utils/form-create-props";
import { doubleBindString } from "../../utils/double-bind";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";
import { FloatInput } from "../atoms/float-input";
import { GtomG, mGtoG } from "../../utils/unit-conversion";

export interface FormCreateShockProfileProps extends FormCreateProps<ShockProfile> {}

import { store } from "../../store";

@observer
export class FormCreateShockProfile extends Component<FormCreateShockProfileProps> {
    @observable public accessor light: number | undefined;
    @observable public accessor medium: number | undefined;
    @observable public accessor severe: number | undefined;
    @observable public accessor duration: number | undefined;
    @observable public accessor xAxisScale: number | undefined;
    @observable public accessor yAxisScale: number | undefined;
    @observable public accessor zAxisScale: number | undefined;
    @observable public accessor label = "";

    private validationId = createUuid();

    constructor(props: FormCreateShockProfileProps) {
        super(props);
        makeObservable(this);
        store.repositoryShockProfiles.validation.create.initializeModel(
            this.validationId,
            this.shockProfileCreate,
        );
    }

    private triggerValidation(): void {
        store.repositoryShockProfiles.validation.create.updateModel(
            this.validationId,
            this.shockProfileCreate,
        );
    }

    @computed private get shockProfileCreate(): ShockProfileCreate {
        const { light, medium, severe, duration, xAxisScale, yAxisScale, zAxisScale, label } = this;

        return {
            light: GtomG(light) ?? 0,
            medium: GtomG(medium) ?? 0,
            severe: GtomG(severe) ?? 0,
            duration: duration ?? 0,
            xAxisScale: xAxisScale ?? 1.0,
            yAxisScale: yAxisScale ?? 1.0,
            zAxisScale: zAxisScale ?? 1.0,
            label,
        };
    }

    @action.bound
    private async createShockProfile(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const { shockProfileCreate } = this;
        const requiredFields = ["light", "medium", "severe", "duration"] as const;
        if (requiredFields.some((key) => shockProfileCreate[key] === undefined)) {
            return;
        }
        const shockProfile = await store.repositoryShockProfiles.create(shockProfileCreate);
        this.reset();
        if (this.props.onCreate) {
            this.props.onCreate(shockProfile);
        }
    }

    @action.bound private reset(): void {
        this.light = undefined;
        this.medium = undefined;
        this.severe = undefined;
        this.duration = undefined;
        this.label = "";
        this.triggerValidation();
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formCreateShockProfile.submit.text")}
                validation={store.repositoryShockProfiles.validation.create}
                validationId={this.validationId}
            />
        );

        return (
            <form onSubmit={this.createShockProfile}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t("formCreateShockProfile.label.label")}
                        {...doubleBindString<FormCreateShockProfile>(this, "label", () =>
                            this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.create.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                    <FloatInput
                        required
                        label={store.i18n.t("formCreateShockProfile.light.label")}
                        precision={3}
                        onChange={(val) => {
                            this.light = val;
                            this.triggerValidation();
                        }}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.create.getFieldValidationState(
                                this.validationId,
                                "light",
                            ),
                            (p) => store.i18n.formatFloat(mGtoG(String(p)), 3),
                        )}
                    />
                    <FloatInput
                        required
                        label={store.i18n.t("formCreateShockProfile.medium.label")}
                        precision={3}
                        onChange={(val) => {
                            this.medium = val;
                            this.triggerValidation();
                        }}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.create.getFieldValidationState(
                                this.validationId,
                                "medium",
                            ),
                            (p) => store.i18n.formatFloat(mGtoG(String(p)), 3),
                        )}
                    />
                    <FloatInput
                        required
                        label={store.i18n.t("formCreateShockProfile.severe.label")}
                        precision={3}
                        onChange={(val) => {
                            this.severe = val;
                            this.triggerValidation();
                        }}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.create.getFieldValidationState(
                                this.validationId,
                                "severe",
                            ),
                            (p) => store.i18n.formatFloat(mGtoG(String(p)), 3),
                        )}
                    />
                    <FloatInput
                        required
                        label={store.i18n.t("formCreateShockProfile.duration.label")}
                        precision={0}
                        onChange={(val) => {
                            this.duration = val;
                            this.triggerValidation();
                        }}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.create.getFieldValidationState(
                                this.validationId,
                                "duration",
                            ),
                            (p) => p,
                        )}
                    />
                    <Separator />
                    <div style={{ position: "relative" }}>
                        <TooltipHost
                            content={store.i18n.t("formCreateShockProfile.axesScaling.tooltip")}
                            calloutProps={{ gapSpace: 0, target: "#axis-icon" }}
                        >
                            <Icon
                                id="axis-icon"
                                styles={{ root: { position: "absolute", right: 0, zIndex: 1 } }}
                                iconName="Info"
                            />
                        </TooltipHost>
                    </div>
                    <FloatInput
                        required
                        label={store.i18n.t("formCreateShockProfile.xAxisScale.label")}
                        precision={6}
                        defaultValue={1.0}
                        onChange={(val) => {
                            this.xAxisScale = val;
                            this.triggerValidation();
                        }}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.create.getFieldValidationState(
                                this.validationId,
                                "xAxisScale",
                            ),
                            (p) => p,
                        )}
                    />
                    <FloatInput
                        required
                        label={store.i18n.t("formCreateShockProfile.yAxisScale.label")}
                        precision={6}
                        defaultValue={1.0}
                        onChange={(val) => {
                            this.yAxisScale = val;
                            this.triggerValidation();
                        }}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.create.getFieldValidationState(
                                this.validationId,
                                "yAxisScale",
                            ),
                            (p) => p,
                        )}
                    />
                    <FloatInput
                        required
                        label={store.i18n.t("formCreateShockProfile.zAxisScale.label")}
                        precision={6}
                        defaultValue={1.0}
                        onChange={(val) => {
                            this.zAxisScale = val;
                            this.triggerValidation();
                        }}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShockProfiles.validation.create.getFieldValidationState(
                                this.validationId,
                                "zAxisScale",
                            ),
                            (p) => p,
                        )}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formCreateShockProfile.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
